import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Ban, AlertTriangle, Server, Users, Lock, FileText, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';
import { contactInfo } from '../config/contactInfo';

const AcceptableUsePolicy: React.FC = () => {
  const sections = [
    {
      icon: ShieldCheck,
      title: '1. Acceptable Use',
      content: [
        'Use the Services only for their intended purpose: healthcare management, clinical documentation, staff administration, and related professional activities',
        'Ensure all information you provide is accurate, current, and not misleading',
        'Comply with all applicable South African laws, regulations, and professional standards, including the National Health Act, HPCSA guidelines, and POPIA',
        'Maintain appropriate professional licences and registrations required to use healthcare-related features',
        'Implement reasonable security measures on your end, including strong passwords and secure devices',
        'Promptly report any security incidents, vulnerabilities, or suspected breaches to BioAxis',
        'Ensure all users with access to your account are authorised and trained on data protection obligations',
        'Respect the intellectual property rights of BioAxis and third parties'
      ]
    },
    {
      icon: Ban,
      title: '2. Prohibited Activities',
      content: [
        'Using the Services for any unlawful, fraudulent, or malicious purpose',
        'Accessing or attempting to access accounts, data, or systems that you are not authorised to use',
        'Uploading, transmitting, or storing malware, viruses, ransomware, or other harmful code',
        'Attempting to reverse-engineer, decompile, disassemble, or otherwise derive the source code of any BioAxis software',
        'Using automated tools, bots, scrapers, or crawlers to access the Services without prior written consent',
        'Reselling, sublicensing, or commercially redistributing the Services or any data obtained through them',
        'Circumventing or attempting to circumvent any security measures, access controls, or usage limits',
        'Using the Services to send unsolicited communications (spam), including bulk emails or messages',
        'Impersonating any person or entity, or falsely representing your affiliation with any person or entity',
        'Storing or transmitting content that is defamatory, obscene, or infringes the rights of others'
      ]
    },
    {
      icon: Lock,
      title: '3. Healthcare Data Obligations',
      content: [
        'You must obtain all necessary consents from patients, staff, or other individuals before entering their personal information into the Services',
        'Patient health records must be handled in accordance with the National Health Act 61 of 2003 and POPIA',
        'You are the Responsible Party (controller) for patient and staff data you process through the Services; BioAxis acts as an Operator (processor)',
        'You must not use the Services to process personal information for purposes beyond what is necessary and lawful',
        'You must maintain appropriate audit trails and access logs as required by healthcare regulations',
        'You must inform your patients about how their data is processed and their rights under POPIA',
        'You must report any data breach involving patient information to the Information Regulator and affected parties as required by POPIA Section 22'
      ]
    },
    {
      icon: Server,
      title: '4. System & Network Security',
      content: [
        'Do not attempt to probe, scan, or test the vulnerability of BioAxis systems without prior written authorisation',
        'Do not attempt to interfere with, disrupt, or degrade the performance of the Services for other users',
        'Do not attempt to bypass rate limits, storage quotas, or other usage restrictions',
        'Do not use the Services in a manner that could overload, damage, or impair our infrastructure',
        'Keep your access credentials (passwords, API keys, tokens) secure and do not share them',
        'Use multi-factor authentication where available and encouraged',
        'Notify BioAxis immediately if you suspect your credentials have been compromised'
      ]
    },
    {
      icon: Users,
      title: '5. Multi-User & Account Management',
      content: [
        'Account holders are responsible for the actions of all users who access the Services under their account',
        'Each user must have their own login credentials; sharing of login credentials is prohibited',
        'Promptly remove access for users who no longer require it (e.g., departed staff members)',
        'Assign appropriate permission levels based on the principle of least privilege',
        'Maintain accurate records of who has access to what data within your organisation',
        'Ensure all users under your account are aware of and comply with this Acceptable Use Policy'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20" style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ShieldCheck className="w-16 h-16 text-primary-400 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Acceptable Use <span className="gradient-text">Policy</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              This policy outlines the acceptable and prohibited uses of BioAxis Services to ensure a safe, secure, and compliant environment for all users.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: February 2026
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Overview */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mb-12"
          >
            <h2 className="text-3xl font-bold mb-6">Overview</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              This Acceptable Use Policy ("AUP") applies to all users of BioAxis Services, including VetAxis, StaffAxis, NoteAxis, and any related products or features. This AUP supplements and forms part of our{' '}
              <Link to="/terms" className="text-primary-400 hover:text-primary-300 underline">Terms of Service</Link>.
            </p>
            <p className="text-gray-300 leading-relaxed">
              By using our Services, you agree to comply with this AUP. Violations may result in warnings, service suspension, account termination, or legal action as appropriate.
            </p>
          </motion.div>

          {/* Policy Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="glass-effect glass-effect-hover p-8 rounded-2xl"
              >
                <div className="flex items-start gap-4 mb-6">
                  <section.icon className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
                  <h3 className="text-2xl font-bold">{section.title}</h3>
                </div>
                <ul className="space-y-3">
                  {section.content.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-300">
                      <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Enforcement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-12"
          >
            <div className="flex items-start gap-4 mb-6">
              <AlertTriangle className="w-8 h-8 text-yellow-400 flex-shrink-0 mt-1" />
              <h3 className="text-2xl font-bold">6. Enforcement & Consequences</h3>
            </div>
            <div className="text-gray-300 space-y-4">
              <p>
                BioAxis reserves the right to investigate suspected violations of this AUP and take appropriate action, which may include:
              </p>
              <ul className="space-y-3">
                {[
                  'Issuing a written warning with a specified remediation period',
                  'Temporarily suspending access to all or part of the Services',
                  'Permanently terminating your account and access to the Services',
                  'Removing or disabling access to content that violates this AUP',
                  'Reporting violations to law enforcement authorities or regulatory bodies (including the Information Regulator and HPCSA) where required or appropriate',
                  'Pursuing legal remedies, including claims for damages',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-400 mt-4">
                Where possible, BioAxis will provide notice before taking enforcement action, except where immediate action is necessary to protect the security of the Services or comply with legal obligations.
              </p>
            </div>
          </motion.div>

          {/* Reporting */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <FileText className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
              <h3 className="text-2xl font-bold">7. Reporting Violations</h3>
            </div>
            <div className="text-gray-300 space-y-4">
              <p>
                If you become aware of any violation of this AUP, please report it to us immediately. Reports can be made via:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Email: <a href={`mailto:${contactInfo.email.support}`} className="text-primary-400 hover:text-primary-300 underline">{contactInfo.email.support}</a></span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Phone: {contactInfo.phone.display}</span>
                </li>
              </ul>
              <p className="text-sm text-gray-400">
                All reports will be investigated promptly and treated confidentially where possible.
              </p>
            </div>
          </motion.div>

          {/* Governing Law */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <Scale className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
              <h3 className="text-2xl font-bold">8. Governing Law</h3>
            </div>
            <div className="text-gray-300 space-y-4">
              <p>
                This AUP is governed by the laws of the Republic of South Africa. Any disputes arising from this AUP shall be resolved in accordance with the dispute resolution provisions in our{' '}
                <Link to="/terms" className="text-primary-400 hover:text-primary-300 underline">Terms of Service</Link>.
              </p>
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-12 text-center"
          >
            <h3 className="text-2xl font-bold mb-4">Questions About This Policy?</h3>
            <p className="text-gray-300 mb-6">
              If you have questions about this Acceptable Use Policy or need clarification on what is permitted, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href={`mailto:${contactInfo.email.support}`}
                whileHover={{ scale: 1.05 }}
                className="btn-primary"
              >
                Email Support
              </motion.a>
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                className="btn-outline"
              >
                Contact Us
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AcceptableUsePolicy;
